import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import useAuth from '../../hooks/useAuth';
import useTheme from '../../hooks/useTheme';

const Layout = ({ children }) => {
    const { user, logout, can } = useAuth();
    const { theme } = useTheme();
    const navigate = useNavigate();
    const location = useLocation();
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [userMenuOpen, setUserMenuOpen] = useState(false);

    const isSuperAdmin = user?.role_slug === 'superadmin';
    const isAdmin = user?.role_slug === 'admin';

    const navItems = [
        { path: '/dashboard',   label: 'Dashboard',     icon: '🏠', show: can('dashboard',   'read') },
        { path: '/users',       label: 'Usuarios',      icon: '👥', show: can('usuarios',    'read') },
        { path: '/roles',       label: 'Roles',         icon: '🔐', show: can('roles',       'read') },
        { path: '/clientes',    label: 'Clientes',      icon: '🏢', show: can('clientes',    'read') },
        { path: '/pqr',         label: 'PQR',           icon: '📋', show: can('pqr',         'read') },
        { path: '/facturacion', label: 'Facturación',   icon: '💰', show: can('facturacion', 'read') },
        { path: '/reportes',    label: 'Reportes',      icon: '📊', show: can('reportes',    'read') },
        { path: '/settings',    label: 'Configuración', icon: '⚙️', show: isSuperAdmin || isAdmin },
    ];

    const visibleItems = navItems.filter(item => item.show);

    const handleLogout = async () => {
        await logout();
        navigate('/login');
    };

    return (
        <div className="flex h-screen overflow-hidden" style={{ backgroundColor: 'var(--color-fondo)' }}>

            {/* Sidebar */}
            <aside
                className={`${sidebarOpen ? 'w-64' : 'w-16'} transition-all duration-300 flex flex-col text-white flex-shrink-0`}
                style={{ backgroundColor: 'var(--color-primary)' }}
            >
                <div className="flex items-center justify-between px-4 py-4 border-b border-white/20">
                    {sidebarOpen && (
                        <div className="flex items-center gap-2">
                            {theme.empresa_logo ? (
                                <img src={theme.empresa_logo} alt="Logo" className="h-8 object-contain" />
                            ) : (
                                <span className="font-bold text-lg">{theme.empresa_nombre}</span>
                            )}
                        </div>
                    )}
                    <button onClick={() => setSidebarOpen(!sidebarOpen)} className="text-white/80 hover:text-white text-xl">
                        {sidebarOpen ? '◀' : '▶'}
                    </button>
                </div>

                <nav className="flex-1 py-4 overflow-y-auto">
                    {visibleItems.map(item => (
                        <Link
                            key={item.path}
                            to={item.path}
                            className={`flex items-center gap-3 px-4 py-3 text-sm transition-colors ${
                                location.pathname === item.path
                                    ? 'bg-white/20 font-semibold'
                                    : 'hover:bg-white/10'
                            }`}
                        >
                            <span className="text-lg flex-shrink-0">{item.icon}</span>
                            {sidebarOpen && <span>{item.label}</span>}
                        </Link>
                    ))}
                </nav>

                <div className="border-t border-white/20 p-4">
                    <div className="flex items-center gap-3">
                        {user?.foto_url ? (
                            <img src={user.foto_url} alt="Foto" className="w-8 h-8 rounded-full object-cover flex-shrink-0" />
                        ) : (
                            <div className="w-8 h-8 rounded-full bg-white/30 flex items-center justify-center text-sm font-bold flex-shrink-0">
                                {user?.nombre?.charAt(0)}
                            </div>
                        )}
                        {sidebarOpen && (
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium truncate">{user?.nombre} {user?.apellido}</p>
                                <p className="text-xs text-white/60 truncate">{user?.role}</p>
                            </div>
                        )}
                    </div>
                </div>
            </aside>

            {/* Main */}
            <div className="flex-1 flex flex-col overflow-hidden">
                <header className="bg-white shadow-sm px-6 py-3 flex items-center justify-between flex-shrink-0">
                    <h2 className="text-gray-700 font-semibold">
                        {navItems.find(i => i.path === location.pathname)?.label || 'CRM'}
                    </h2>
                    <div className="relative">
                        <button
                            onClick={() => setUserMenuOpen(!userMenuOpen)}
                            className="flex items-center gap-2 text-sm text-gray-700 hover:text-gray-900"
                        >
                            {user?.foto_url ? (
                                <img src={user.foto_url} alt="Foto" className="w-8 h-8 rounded-full object-cover" />
                            ) : (
                                <div
                                    className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold"
                                    style={{ backgroundColor: 'var(--color-primary)' }}
                                >
                                    {user?.nombre?.charAt(0)}
                                </div>
                            )}
                            <span>{user?.nombre}</span>
                            <span className="text-gray-400">▾</span>
                        </button>
                        {userMenuOpen && (
                            <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-100 z-50">
                                <Link
                                    to="/profile"
                                    className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
                                    onClick={() => setUserMenuOpen(false)}
                                >
                                    Mi perfil
                                </Link>
                                <button
                                    onClick={handleLogout}
                                    className="w-full text-left px-4 py-2.5 text-sm text-red-600 hover:bg-red-50"
                                >
                                    Cerrar sesión
                                </button>
                            </div>
                        )}
                    </div>
                </header>

                <main className="flex-1 overflow-auto p-6">
                    {children}
                </main>
            </div>
        </div>
    );
};

export default Layout;
